import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DefaultValues,
  FieldValues,
  UseFormReturn,
  useForm,
  Path,
  PathValue,
  useWatch,
} from 'react-hook-form';
import {
  ExtendedDialog,
  DialogTrackingNames,
} from 'ui-component/extended/ExtendedDialog';
import * as Yup from 'yup';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { PermissionScope } from 'types/apps';
import { formatListWithOr, handleErr } from 'utils/functions';
import { useNavigate } from 'react-router-dom';
import useSnackbar from 'hooks/useSnackbar';
import _ from 'lodash';
import { Box, Chip, DialogProps, Stack, Typography } from '@mui/material';
import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import InlineUploadDocumentsDropZone, {
  UnsavedStockDocument,
} from 'ui-component/StockDocuments/UploadDocuments/InlineUploadDocumentsDropZone';
import {
  useCreateStockDocumentRelatedObjectMutation,
  useStockDocumentCreateWithAiMutation,
} from 'store/slices/clientV2/stockDocuments';
import { useCreateStockDocsV2 } from 'hooks/useCreateStockDocsV2';
import { HookFormDocuments } from 'ui-component/HookFormComponents/HookFormDocuments';
import HookFormToggleAlert from 'ui-component/HookFormComponents/HookFormToggleAlert';
import { IconSparkles } from '@tabler/icons-react';
import { StockDocTypeLabels } from 'types/inventory';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';
import { RequestPersistenceType, useAsyncRequest } from 'hooks/useAsyncRequest';
import { useDispatch } from 'react-redux';
import { apiSlice, ApiTagTypes } from 'store/slices/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import LoadingAlert from 'ui-component/extended/LoadingAlert';

type BaseCreateRecordDialogProps<
  TFormValues extends FieldValues = FieldValues & {
    attachments?: UnsavedStockDocument[];
  },
  TSubmitData = object
> = {
  formConstants: {
    [id: string]: {
      id: string;
      label: string;
    };
  };
  validationSchema: Yup.ObjectSchema<any>;
  newRecordDefaults?: Partial<TFormValues>;
  onSave?: (record: object) => void;
  onSaveClientV2?: (recordId: string) => void;
  useCreateMutation: UseMutation<any>;
  preSubmit?: (data: TFormValues) => TSubmitData;
  asyncPreSubmit?: (data: TFormValues) => Promise<TSubmitData>;
  navigateToPath?: string;
  goToCreatedRecord?: boolean;
  otherProps?: object;
  submitAsPayload?: boolean;
  arrayBody?: boolean;
  trackingNames?: DialogTrackingNames;
  permissionScope?: PermissionScope;
  submitButtonCopy?: string;
  recordType?: ALL_RECORD_TYPE_IDS;
  allowAttachments?: boolean;
  isSubmitting?: boolean;
};

type CreateRecordDialogProps<
  TFormValues extends FieldValues = FieldValues,
  TSubmitData = any
> = BaseCreateRecordDialogProps<TFormValues, TSubmitData> &
  (
    | {
        overrideTitle: string;
        overrideSuccessSnackbar: string;
        recordName?: string;
      }
    | {
        overrideTitle?: string;
        overrideSuccessSnackbar?: string;
        recordName: string;
      }
  );

export default function useCreateRecordDialog<
  TFormValues extends FieldValues = FieldValues,
  TSubmitData = any
>(props: CreateRecordDialogProps<TFormValues, TSubmitData>) {
  const methods = useForm<TFormValues>({
    defaultValues: props.newRecordDefaults as DefaultValues<TFormValues>,
    resolver: yupResolver(props.validationSchema),
    mode: 'all',
  });

  return {
    methods,
    control: methods.control,
    errors: methods.formState.errors,
    watch: methods.watch,
    dialogProps: {
      props,
      methods,
    },
    BaseCreateRecordDialog: BaseCreateRecordDialog<TFormValues, TSubmitData>,
  };
}

const BaseCreateRecordDialog = <
  TFormValues extends FieldValues & {
    attachments?: UnsavedStockDocument[];
  } = FieldValues & { attachments?: UnsavedStockDocument[] },
  TSubmitData = any
>({
  children,
  open,
  onClose,
  dialogProps,
  confirmClose,
  maxWidth = 'sm',
}: {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  dialogProps: {
    props: CreateRecordDialogProps<TFormValues, TSubmitData>;
    methods: UseFormReturn<TFormValues>;
  };
  confirmClose?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  isSubmitting?: boolean;
}) => {
  const tempDocaiLite = useFeature(FeatureFlags.tempDocaiLite).on;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { dispatchSuccessSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const recordTypeDefinition = dialogProps.props.recordType
    ? ALL_RECORD_TYPES[dialogProps.props.recordType]
    : undefined;

  const [create, { isLoading: isLoadingCreate }] =
    dialogProps.props.useCreateMutation();

  const [createWithAi, { isFetching: isLoadingCreateWithAi, jobMessage }] =
    useAsyncRequest(
      useStockDocumentCreateWithAiMutation,
      RequestPersistenceType.component
    );

  const { isLoading: isLoadingCreateStockDoc } = useCreateStockDocsV2();

  const [
    createStockDocumentRelatedObject,
    { isLoading: isLoadingCreateStockDocumentRelatedObject },
  ] = useCreateStockDocumentRelatedObjectMutation();

  const handleClose = () => {
    onClose();
    dialogProps.methods.reset();
    setSubmitError(null);
  };

  const watchAttachments = useWatch({
    control: dialogProps.methods.control,
    name: 'attachments' as Path<TFormValues>,
  });
  const watchCreateWithAi = useWatch({
    control: dialogProps.methods.control,
    name: 'createWithAi' as Path<TFormValues>,
  });

  const hideCreateWithAi =
    !tempDocaiLite || !recordTypeDefinition?.createWithAiDocTypes;
  const canCreateWithAi =
    tempDocaiLite &&
    recordTypeDefinition?.createWithAiDocTypes &&
    _.intersection(
      recordTypeDefinition.createWithAiDocTypes,
      _.map(watchAttachments || [], (d: UnsavedStockDocument) => d.documentType)
    ).length > 0;
  const createWithAiDoc = _.chain(watchAttachments)
    .filter((d: UnsavedStockDocument) =>
      recordTypeDefinition?.createWithAiDocTypes?.includes(d.documentType)
    )
    .first()
    .value();
  const invalidCreateWithAiDoc =
    !canCreateWithAi && !!watchAttachments
      ? (_.first(watchAttachments) as UnsavedStockDocument | undefined)
      : null;

  // Helper function to get tags to invalidate based on the mutation's invalidatesTags
  const getTagsToInvalidate = (
    recordId?: string
  ): TagDescription<ApiTagTypes>[] => {
    const tags: TagDescription<ApiTagTypes>[] = [];

    if (!recordTypeDefinition?.createWithAiInvalidateTags) {
      return tags;
    }

    _.forEach(recordTypeDefinition.createWithAiInvalidateTags, (tag) => {
      if (recordId) {
        tags.push({ type: tag, id: recordId });
      }

      tags.push({ type: tag, id: 'LIST' });
      tags.push({ type: tag, id: 'PARTIAL-LIST' });
    });

    return tags;
  };

  const onSubmit = async (data: TFormValues) => {
    // handle create with ai
    if (data.createWithAi && recordTypeDefinition?.flagshipModel) {
      try {
        const dataWithoutAttachments = _.omit(data, [
          'attachments',
          'createWithAi',
        ]) as TFormValues;

        const response = await createWithAi({
          stockDocumentId: createWithAiDoc.id,
          request: {
            recordObjectType: recordTypeDefinition?.flagshipModel,
            relatedStockDocuments: _.map(watchAttachments, (d) => d.id),
            ...dataWithoutAttachments,
            ...(dialogProps.props.otherProps || {}),
          },
        });

        const recordId = _.get(response, 'createdIds[0]', '');

        // Invalidate the same tags that the useCreateMutation would invalidate
        const tagsToInvalidate = getTagsToInvalidate(recordId);
        if (tagsToInvalidate.length > 0) {
          dispatch(apiSlice.util.invalidateTags(tagsToInvalidate as any));
        }

        if (dialogProps.props.onSaveClientV2) {
          dialogProps.props.onSaveClientV2(recordId as string);
        }
        handleClose();
        dispatchSuccessSnackbar(
          dialogProps.props.overrideSuccessSnackbar ??
            `${dialogProps.props.recordName} Created`
        );

        if (
          dialogProps.props.goToCreatedRecord &&
          dialogProps.props.navigateToPath
        ) {
          navigate(`${dialogProps.props.navigateToPath}/${recordId}`);
        }
        return;
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          setSubmitError(errMessage);
        });
        return;
      }
    }

    try {
      const docsToUpload = data.attachments as UnsavedStockDocument[];
      const dataWithoutAttachments = _.omit(data, [
        'attachments',
        'createWithAi',
      ]) as TFormValues;
      const preSubmitData = dialogProps.props.asyncPreSubmit
        ? await dialogProps.props.asyncPreSubmit(dataWithoutAttachments)
        : dialogProps.props.preSubmit
        ? dialogProps.props.preSubmit(dataWithoutAttachments)
        : undefined;
      const record = await create(
        dialogProps.props.arrayBody
          ? preSubmitData ?? [dataWithoutAttachments]
          : {
              ...(dialogProps.props.otherProps || {}),
              ...(dialogProps.props.submitAsPayload
                ? {
                    payload: preSubmitData ?? dataWithoutAttachments,
                  }
                : preSubmitData ?? dataWithoutAttachments),
            }
      ).unwrap();
      const recordId = _.get(record, 'id', _.get(record, 'createdIds[0]', ''));

      // create/link stock documents
      if (recordTypeDefinition && docsToUpload) {
        const joins = _.map(docsToUpload, (d) => ({
          stockDocument: d.id as string,
          relatedObjectId: recordId,
          relatedObjectType: recordTypeDefinition.flagshipModel,
        }));

        await createStockDocumentRelatedObject(joins).unwrap();
      }

      if (dialogProps.props.onSave) {
        dialogProps.props.onSave(record as object);
      }
      if (dialogProps.props.onSaveClientV2) {
        dialogProps.props.onSaveClientV2(recordId as string);
      }
      handleClose();
      dispatchSuccessSnackbar(
        dialogProps.props.overrideSuccessSnackbar ??
          `${dialogProps.props.recordName} Created`
      );
      if (
        dialogProps.props.goToCreatedRecord &&
        dialogProps.props.navigateToPath
      ) {
        navigate(`${dialogProps.props.navigateToPath}/${recordId}`);
      }
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        setSubmitError(errMessage);
      });
    }
  };

  return (
    <ExtendedDialog
      onSubmit={dialogProps.methods.handleSubmit(onSubmit)}
      formSubmitError={submitError}
      submitButtonCopy={dialogProps.props.submitButtonCopy || 'Save'}
      isSubmitting={
        isLoadingCreate ||
        isLoadingCreateStockDoc ||
        isLoadingCreateStockDocumentRelatedObject ||
        isLoadingCreateWithAi ||
        dialogProps.props.isSubmitting
      }
      confirmClose={confirmClose}
      open={open}
      isForm
      onCloseDialog={handleClose}
      title={
        dialogProps.props.overrideTitle ??
        `Create ${dialogProps.props.recordName}`
      }
      trackingNames={dialogProps.props.trackingNames}
      permissionScope={dialogProps.props.permissionScope}
      maxWidth={maxWidth}
      inlineEditDialog
    >
      {children}
      {dialogProps.props.allowAttachments && dialogProps.props.recordType && (
        <Box sx={{ mt: 2 }}>
          <HookFormDocuments
            name="attachments"
            label="Attachments"
            errors={dialogProps.methods.formState.errors}
            control={dialogProps.methods.control}
          />
          <InlineUploadDocumentsDropZone
            boxSx={{
              mt: 2,
            }}
            recordType={dialogProps.props.recordType}
            onDialogCloseWithoutSave={(docs) => {
              const currentAttachments =
                dialogProps.methods.getValues(
                  'attachments' as Path<TFormValues>
                ) || [];
              dialogProps.methods.setValue(
                'attachments' as Path<TFormValues>,
                [...currentAttachments, ...docs] as PathValue<
                  TFormValues,
                  Path<TFormValues>
                >
              );
            }}
          />
          {!hideCreateWithAi && (
            <HookFormToggleAlert
              name="createWithAi"
              label={
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <IconSparkles />
                  <Typography variant="h5">
                    Create {recordTypeDefinition?.singularName} Ailana
                  </Typography>
                  <Chip label="BETA" size="small" color="primary" />
                </Stack>
              }
              control={dialogProps.methods.control}
              errors={dialogProps.methods.formState.errors}
              severity="success"
              variant="standard"
              sx={{ mt: 2 }}
              disabled={!canCreateWithAi}
            >
              {!canCreateWithAi &&
                !invalidCreateWithAiDoc &&
                `Upload at least one ${formatListWithOr(
                  _.map(
                    recordTypeDefinition?.createWithAiDocTypes || [],
                    (d) => StockDocTypeLabels[d]
                  )
                )} document to automatically create a ${
                  recordTypeDefinition?.singularName
                } with Ailana`}
              {!canCreateWithAi &&
                invalidCreateWithAiDoc &&
                `Upload at least one ${formatListWithOr(
                  _.map(
                    recordTypeDefinition?.createWithAiDocTypes || [],
                    (d) => StockDocTypeLabels[d]
                  )
                )} document to automatically create a ${
                  recordTypeDefinition?.singularName
                } with AI. ${invalidCreateWithAiDoc.file.name} is a ${
                  StockDocTypeLabels[invalidCreateWithAiDoc.documentType]
                }, which cannot be used to create a ${
                  recordTypeDefinition?.singularName
                } with Ailana.`}
              {canCreateWithAi &&
                !watchCreateWithAi &&
                `Enable Create Ailana to automatically generate a ${recordTypeDefinition?.singularName} from the contents of ${createWithAiDoc.file.name}`}
              {canCreateWithAi &&
                watchCreateWithAi &&
                `On Save, Ailana will analyze the contents of ${createWithAiDoc.file.name} and automatically generate a ${recordTypeDefinition?.singularName}. Please be patient as this may take a minute.`}
            </HookFormToggleAlert>
          )}
          {isLoadingCreateWithAi && (
            <LoadingAlert
              variant="outlined"
              severity="info"
              title="Creating with Ailana"
              sx={{ mt: 2 }}
            >
              {!!jobMessage && jobMessage}
            </LoadingAlert>
          )}
        </Box>
      )}
    </ExtendedDialog>
  );
};
